import createStore from "zustand";
import { produce } from "immer";
import { useCallback } from "react";

import { SupportedPublicPerformanceTierKey } from "shared/SelectPerformance/useGetPerformanceTiers";

type ActualQueryId = number;
type QueryIdentifier = ActualQueryId | "default";

type PerformanceMap = Record<
  ActualQueryId,
  SupportedPublicPerformanceTierKey
> & {
  default?: SupportedPublicPerformanceTierKey;
};

// Keep the last used performance for each query in localStorage
const LAST_PERFORMANCE_MAP = "lastPerformanceMap";

const localStorage_LastPerformanceMap = {
  get(): PerformanceMap {
    try {
      const jsonString = localStorage.getItem(LAST_PERFORMANCE_MAP);
      if (jsonString === null) {
        return {};
      }

      const object = JSON.parse(jsonString);
      if (typeof object !== "object" && object === null) {
        return {};
      }

      return object;
    } catch (error) {
      return {};
    }
  },
  set(lastPerformanceMap: PerformanceMap) {
    localStorage.setItem(
      LAST_PERFORMANCE_MAP,
      JSON.stringify(lastPerformanceMap)
    );
  },
  setOne(
    queryId: QueryIdentifier,
    performance: SupportedPublicPerformanceTierKey
  ) {
    const { get, set } = localStorage_LastPerformanceMap;

    set({
      ...get(),
      [queryId]: performance,
    });
  },
};

// Store
const performanceMapStore = createStore<PerformanceMap>(() =>
  localStorage_LastPerformanceMap.get()
);
const usePerformanceMapStore = performanceMapStore;

// Getters
function getPerformanceForQuery(
  performanceMap: PerformanceMap,
  queryId: QueryIdentifier | undefined
) {
  if (queryId === undefined) {
    return undefined;
  }

  const performance = performanceMap[queryId];

  if (performance === undefined) {
    return undefined;
  }

  return performance;
}

export function getDefaultPerformance_NoValidation() {
  return getPerformanceForQuery(performanceMapStore.getState(), "default");
}

// Setter
export function setPerformanceForQuery(
  queryId: QueryIdentifier,
  performance: SupportedPublicPerformanceTierKey
) {
  performanceMapStore.setState(
    produce((lastPerformanceMap) => {
      lastPerformanceMap[queryId] = performance;
    })
  );
  localStorage_LastPerformanceMap.setOne(queryId, performance);
}

// Getter hook
export function usePerformanceForQuery_NoValidation(
  queryId: QueryIdentifier | undefined
): SupportedPublicPerformanceTierKey | undefined {
  const selectPerformanceForQuery = useCallback(
    (lastPerformanceMap: PerformanceMap) =>
      getPerformanceForQuery(lastPerformanceMap, queryId),
    [queryId]
  );

  return usePerformanceMapStore(selectPerformanceForQuery);
}

// Setter hook
export function useSetPerformanceForQuery(
  queryId: QueryIdentifier | undefined
): (performance: SupportedPublicPerformanceTierKey) => void {
  return useCallback(
    (performance: SupportedPublicPerformanceTierKey) => {
      if (queryId === undefined) {
        return;
      }

      setPerformanceForQuery(queryId, performance);
    },
    [queryId]
  );
}
